import { PropsWithChildren, useMemo, useRef, useState } from 'react';
import { getMaxZIndex, getOffset } from 'utils/DomUtils';
import classNames from 'classnames';

export interface IRippleProps {
  isIconButton?: boolean;
  className?: string;
}

export function Ripple(props: PropsWithChildren<IRippleProps>) {
  const [rippleData, setRippleData] = useState({
    style: {},
    start: false,
  });
  const holder = useRef<HTMLDivElement>(null);
  const ripple = useRef(null);
  let timerId: number;
  const animationTimeout = props.isIconButton ? 700 : 450;
  const iconButtonClassName = 'dp-ripple-icon-button-holder';

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const isIconButtonClicked = (event.target as Element).classList.contains(iconButtonClassName);
    const isBubbledUp = !(event.currentTarget as Element).classList.contains(iconButtonClassName);
    if (isIconButtonClicked && isBubbledUp) return;
    const zIndex = getMaxZIndex();
    setRippleData(preData => ({ ...preData, start: false }));
    clearTimeout(timerId);
    if (!holder.current) return;
    const offset = getOffset(holder.current);
    const left = props.isIconButton ? holder.current.offsetWidth / 2 : event.clientX - offset.left;
    const top = props.isIconButton ? holder.current.offsetHeight / 2 : event.clientY - offset.top;
    const borderRadius = props.isIconButton ? '100%' : undefined;
    const updatedStyle = {
      top: `${top}px`,
      left: `${left}px`,
      zIndex,
      borderRadius,
    };
    setRippleData({ style: updatedStyle, start: true });
    timerId = window.setTimeout(() => {
      setRippleData(preData => ({ ...preData, start: false }));
    }, animationTimeout);
  };

  const rippleView = useMemo(() => {
    if (!rippleData.start) return null;
    return (
      <>
        {holder.current && (
          <style
            dangerouslySetInnerHTML={{
              __html: `
                    @keyframes dp-ripple-frames {
                      0%   { width: 0px; height: ${props.isIconButton ? 0 : holder.current.clientHeight * 2}px; }      
                      100% { width: ${holder.current.clientWidth * 2}px; 
                      height: ${holder.current.clientHeight * 2}px;
                      opacity:0;}
                    }`,
            }}
          ></style>
        )}
        <span ref={ripple} className="dp-ripple" style={rippleData.style}></span>
      </>
    );
  }, [rippleData, props.isIconButton]);

  const holderClassName = useMemo(() => {
    return classNames(props.className, 'dp-ripple-holder', { [iconButtonClassName]: props.isIconButton });
  }, [props.className, props.isIconButton]);

  return (
    <div ref={holder} className={holderClassName} onMouseDown={handleMouseDown}>
      {rippleView}
      {props.children}
    </div>
  );
}
